import { type CreateInvoiceLineBody, type InvoiceLineType } from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { Plus, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

export interface LineItem {
  _key: string
  line_type: InvoiceLineType
  description: string
  quantity: number
  unit_price_xof: number
  tax_rate: number
}

// tax_rate est exprimé en pourcentage (0-100), conforme au contrat backend
// (numeric(5,2) avec validation gte=0,lte=100). Le backend calcule
// tax = excl * rate / 100.
export function emptyLine(): LineItem {
  return {
    _key: crypto.randomUUID(),
    line_type: 'other',
    description: '',
    quantity: 1,
    unit_price_xof: 0,
    tax_rate: 18,
  }
}

export function toCreateInvoiceLine(item: LineItem): CreateInvoiceLineBody {
  return {
    line_type: item.line_type,
    description: item.description,
    quantity: item.quantity,
    unit_price: Math.round(item.unit_price_xof * 100),
    tax_rate: item.tax_rate,
  }
}

const LINE_TYPES: InvoiceLineType[] = ['freight', 'storage', 'surcharge', 'discount', 'other']

interface Props {
  items: LineItem[]
  onChange: (items: LineItem[]) => void
}

export function LineItemsEditor({ items, onChange }: Props) {
  const { t } = useTranslation('finance')

  function update(key: string, patch: Partial<LineItem>) {
    onChange(items.map((it) => (it._key === key ? { ...it, ...patch } : it)))
  }

  function remove(key: string) {
    onChange(items.filter((it) => it._key !== key))
  }

  const subtotalXOF = items.reduce((acc, it) => acc + it.quantity * it.unit_price_xof, 0)
  const taxXOF = items.reduce(
    (acc, it) => acc + (it.quantity * it.unit_price_xof * it.tax_rate) / 100,
    0,
  )
  const totalXOF = subtotalXOF + taxXOF

  return (
    <div className="flex flex-col gap-3">
      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full min-w-[700px] text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/60 text-xs uppercase text-muted-foreground">
              <th className="w-[140px] px-4 py-3 text-left">
                {t('line.type', { defaultValue: 'Type' })}
              </th>
              <th className="px-4 py-3 text-left">
                {t('line.description', { defaultValue: 'Description' })}
              </th>
              <th className="w-[80px] px-4 py-3 text-right">
                {t('line.qty', { defaultValue: 'Qté' })}
              </th>
              <th className="w-[140px] px-4 py-3 text-right">
                {t('line.unit_price', { defaultValue: 'P.U. (XOF)' })}
              </th>
              <th className="w-[80px] px-4 py-3 text-right">
                {t('line.tax_pct', { defaultValue: 'TVA %' })}
              </th>
              <th className="w-[130px] px-4 py-3 text-right">
                {t('line.total_incl', { defaultValue: 'Total TTC' })}
              </th>
              <th className="w-10" />
            </tr>
          </thead>
          <tbody>
            {items.map((item) => {
              const lineTotal = item.quantity * item.unit_price_xof * (1 + item.tax_rate / 100)
              return (
                <tr key={item._key} className="border-t border-border">
                  <td className="px-4 py-2">
                    <Select
                      value={item.line_type}
                      onChange={(e) =>
                        update(item._key, { line_type: e.target.value as InvoiceLineType })
                      }
                    >
                      {LINE_TYPES.map((lt) => (
                        <option key={lt} value={lt}>
                          {t(`line_type.${lt}`, { defaultValue: lt })}
                        </option>
                      ))}
                    </Select>
                  </td>
                  <td className="px-4 py-2">
                    <Input
                      value={item.description}
                      onChange={(e) => update(item._key, { description: e.target.value })}
                      placeholder={t('line.description_placeholder', {
                        defaultValue: 'Description de la prestation…',
                      })}
                    />
                  </td>
                  <td className="px-4 py-2">
                    <Input
                      type="number"
                      inputMode="numeric"
                      min={1}
                      step={1}
                      value={item.quantity || ''}
                      onChange={(e) =>
                        update(item._key, { quantity: Math.max(1, Number(e.target.value)) })
                      }
                      className="text-right"
                    />
                  </td>
                  <td className="px-4 py-2">
                    <Input
                      type="number"
                      inputMode="numeric"
                      min={0}
                      step={1}
                      value={item.unit_price_xof || ''}
                      onChange={(e) =>
                        update(item._key, { unit_price_xof: Math.max(0, Number(e.target.value)) })
                      }
                      className="text-right"
                    />
                  </td>
                  <td className="px-4 py-2">
                    <Input
                      type="number"
                      inputMode="numeric"
                      min={0}
                      max={100}
                      step={1}
                      value={item.tax_rate || ''}
                      onChange={(e) =>
                        update(item._key, {
                          tax_rate: Math.min(100, Math.max(0, Number(e.target.value))),
                        })
                      }
                      className="text-right"
                    />
                  </td>
                  <td className="px-4 py-2 text-right font-medium tabular-nums text-foreground">
                    {Math.round(lineTotal).toLocaleString('fr-SN')}
                  </td>
                  <td className="px-2 py-2">
                    <button
                      type="button"
                      disabled={items.length === 1}
                      onClick={() => remove(item._key)}
                      className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive disabled:pointer-events-none disabled:opacity-30"
                      aria-label={t('line.remove', { defaultValue: 'Supprimer la ligne' })}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-start justify-between gap-4">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onChange([...items, emptyLine()])}
        >
          <Plus className="h-4 w-4" />
          {t('line.add', { defaultValue: 'Ajouter une ligne' })}
        </Button>

        <div className="flex flex-col items-end gap-1 text-sm">
          <div className="flex gap-8 text-muted-foreground">
            <span>{t('line.subtotal', { defaultValue: 'Sous-total HT' })}</span>
            <span className="min-w-[100px] text-right tabular-nums">
              {Math.round(subtotalXOF).toLocaleString('fr-SN')} XOF
            </span>
          </div>
          <div className="flex gap-8 text-muted-foreground">
            <span>{t('line.tax', { defaultValue: 'TVA' })}</span>
            <span className="min-w-[100px] text-right tabular-nums">
              {Math.round(taxXOF).toLocaleString('fr-SN')} XOF
            </span>
          </div>
          <div className="flex gap-8 border-t border-border pt-1 font-semibold text-foreground">
            <span>{t('invoice.total', { defaultValue: 'Total TTC' })}</span>
            <span className="min-w-[100px] text-right tabular-nums">
              {Math.round(totalXOF).toLocaleString('fr-SN')} XOF
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}
